import {
  pollUpdateEmailStatus,
  updateEmail,
  type UpdateEmailInput,
  type UpdateProfileOutput,
  UpdateProfileStatus,
  useCustomerDetails,
} from '@egw/api'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { zodResolver } from '@hookform/resolvers/zod'
import { type FC, useCallback, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { z, type ZodType } from 'zod'
import {
  UpdateEmailField,
  type UpdateEmailForm,
  type UpdateEmailProps,
  type UpdateEmailStep,
  UpdateEmailStepEnum,
} from '../../types/email/email-types'
import { ChangeEmailStep } from './change-email-step'
import { ConfirmEmailStep } from './confirm-email-step'
import { ErrorStep } from './error-step'
import { VerifyEmailStep } from './verify-email-step'

const log = new Logger('Update Email')

export const UpdateEmail: FC<UpdateEmailProps> = ({ setModalTitle, closeModal }) => {
  const { t } = useTranslation('features-settings')

  const [step, setStep] = useState<UpdateEmailStepEnum>(UpdateEmailStepEnum.CHANGE)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { refetch: refetchCustomerDetails } = useCustomerDetails()

  const onErrorCb = useCallback((e: unknown) => {
    log.error('On Error CB invoked ', e)
    // change to error modal step or launch toast here
    setStep(UpdateEmailStepEnum.ERROR)
  }, [])

  const onSuccessCb = async (data: UpdateProfileOutput): Promise<void> => {
    if (data.__typename === 'UpdateProfileSuccess') {
      log.info('On Success CB', data)
      if (data.status === UpdateProfileStatus.VERIFICATION_SENT) {
        setStep(UpdateEmailStepEnum.VERIFY)
        setModalTitle(t(`update_email.${UpdateEmailStepEnum.VERIFY}.title`))
      }
      if (data.status === UpdateProfileStatus.PENDING_VERIFICATION) {
        // Will start polling API
        log.info('Pending Verification')
        await pollUpdateEmailStatus().then(onSuccessCb).catch(onErrorCb)
      }
      if (data.status === UpdateProfileStatus.COMPLETE) {
        if (step !== UpdateEmailStepEnum.CONFIRM) {
          void refetchCustomerDetails()
          setStep(UpdateEmailStepEnum.CONFIRM)
          setModalTitle(t(`update_email.${UpdateEmailStepEnum.CONFIRM}.title`))
        }
      }
      if (data.status === UpdateProfileStatus.FAILED) onErrorCb(data)
    } else {
      onErrorCb(data)
    }
  }

  const updateEmailValidationSchema: ZodType<UpdateEmailForm> = z.object({
    [UpdateEmailField.EMAIL]: z.string({
      required_error: t(`update_email.${step}.required`),
    }),
    [UpdateEmailField.OTP_CODE]: z
      .string({
        required_error: t(`update_email.${step}.otp_required`),
      })
      .length(6, t(`update_email.${step}.otp_required`)),
  })

  const formMethods = useForm<UpdateEmailForm>({
    defaultValues: {
      [UpdateEmailField.EMAIL]: '',
      [UpdateEmailField.OTP_CODE]: '',
    },
    resolver: zodResolver(updateEmailValidationSchema),
  })

  const onSubmit = async (): Promise<void> => {
    const updateEmailInput: Partial<UpdateEmailInput> = {
      code: step === UpdateEmailStepEnum.VERIFY ? formMethods.getValues(UpdateEmailField.OTP_CODE) : undefined,
      email: step === UpdateEmailStepEnum.CHANGE ? formMethods.getValues(UpdateEmailField.EMAIL) : undefined,
    }

    setIsLoading(true)

    try {
      await updateEmail(updateEmailInput).then(onSuccessCb).catch(onErrorCb)
    } catch (error) {
      onErrorCb(error)
    } finally {
      setIsLoading(false)
    }
  }

  const resendCode = (): void => {
    log.info('Resend Code invoked')
    setIsLoading(true)
    try {
      updateEmail({
        resendVerification: true,
        email: formMethods.getValues(UpdateEmailField.EMAIL),
      }).catch(onErrorCb)
    } catch (error) {
      onErrorCb(error)
    } finally {
      setIsLoading(false)
    }
  }

  const props: UpdateEmailStep = {
    isLoading,

    ctaOnClick: onSubmit,
    resendCode,
    closeModal,
  }

  return (
    <FormProvider {...formMethods}>
      <form id="updateEmail">
        {step === UpdateEmailStepEnum.CHANGE && <ChangeEmailStep {...props} />}
        {step === UpdateEmailStepEnum.VERIFY && <VerifyEmailStep {...props} />}
        {step === UpdateEmailStepEnum.CONFIRM && <ConfirmEmailStep {...props} />}
        {step === UpdateEmailStepEnum.ERROR && <ErrorStep />}
      </form>
    </FormProvider>
  )
}
